import React, { useEffect, useState } from 'react';
import { GrainGradient } from '@paper-design/shaders-react';

/**
 * La mancha de color que hace de fondo, con la paleta de la marca.
 *
 * `GrainGradient` es de Paper Design (Apache-2.0, gratis en npm; no hace falta
 * el envoltorio pago de 21st).
 *
 * LA PALETA NO SE ELIGIÓ: YA ERA LA NUESTRA. `hsl(14,100%,57%)` es casi
 * exactamente el naranja de la marca (#FF5722) y `hsl(340,82%,52%)` es el
 * magenta que ya usa la cabecera de la página pública de reservas. Por eso la
 * mancha no se lee como un adorno pegado encima: es el color del producto.
 *
 * VIVE ACÁ Y NO ADENTRO DE LandingDos porque lo usan también las tres pantallas
 * de auth —login, registro y recuperar contraseña—, que antes tenían su propio
 * degradado y tres esferas borroseadas que no existían en ningún otro lado. Con
 * la configuración escrita en dos lugares, los fondos se separan: alcanza con
 * que alguien toque un `offset` de un lado. Acá hay uno solo.
 *
 * LA POSICIÓN ES UNA SOLA PARA TODA LA APP, y está más abajo con el porqué.
 * Cada pantalla igual la pasa por `posicion`, que es el punto donde volvería a
 * diferenciarse si alguna vez hace falta.
 *
 * LO QUE CUESTA ES EL ÁREA, NO LA VELOCIDAD: el shader redibuja el lienzo
 * entero en cada cuadro, así que bajarle `speed` no ahorra nada. La perilla de
 * rendimiento es `maxPixelCount`, documentada junto a PRESUPUESTO.
 */

/* LA POSICIÓN DEL FONDO, UNA SOLA PARA TODA LA APP.

   `shape="corners"` reparte cuatro masas de color en las esquinas y deja el
   centro limpio. Sin correrlo y a escala completa, cae donde tiene que caer.


   POR QUÉ YA NO HAY UNA VERSIÓN GIRADA PARA LA LANDING

   Durante un tiempo la landing usó `rotation: 90`, que muda las masas a la
   otra diagonal —arriba-izquierda y abajo-derecha—. No fue un capricho: el
   hero de entonces tenía el texto pegado a la IZQUIERDA, y ahí el color le
   caía justo debajo. Medido, 79% de saturación detrás del texto chico; girado,
   2%. Antes de eso se barrieron 45 combinaciones de `offset` y `scale` sin
   que ninguna cumpliera las dos condiciones —limpio detrás del texto y color
   tocando los bordes—, y se descartaron un velo blanco y una placa de vidrio
   por resolver la lectura tapando el fondo, que es desteñir justo lo que hace
   linda a la página.

   El hero se rehízo CENTRADO, así que el texto ya no vive en ninguna esquina y
   el problema desapareció con él. Se vuelve al reparto original, que es el que
   ya usaban las pantallas de auth: mismo fondo en toda la aplicación, una sola
   constante, sin nada que mantener sincronizado.

   Si alguna vez vuelve un bloque de texto contra una esquina, el giro está a
   un parámetro: /dev/fondos?shape=corners&rot=90 y se vuelve a medir.

   (También se probó `shape="sphere"`, que medía todavía mejor pero dibuja una
   pelota con un canto circular durísimo: gana la medición y pierde el aspecto.
   Difuso importa.) */
const FONDO = { offsetX: 0, offsetY: 0, scale: 1 };

/* Los cuatro nombres apuntan al MISMO objeto a propósito. Existían cuatro
   configuraciones cuando la landing necesitaba lo contrario que el login;
   ahora que coinciden, mantenerlas separadas es garantizar que se despeguen.
   Se conservan los nombres para no tocar los cuatro archivos que los importan.

   Nota: con ancho y angosto iguales, `usePantallaAncha()` ya no decide nada
   para el fondo. Se deja porque es el punto donde volvería a diferenciarse. */
export const FONDO_ANCHO = FONDO;
export const FONDO_ANGOSTO = FONDO;
export const FONDO_AUTH_ANCHO = FONDO;
export const FONDO_AUTH_ANGOSTO = FONDO;

/* EL RESPALDO: el mismo dibujo, aproximado con degradados de CSS.
   
   Está pintado desde el primer cuadro, debajo del canvas. El shader es WebGL:
   necesita que llegue el JS, crear el contexto y compilar. Medido con el CPU
   frenado 8x, la tarjeta aparecía a los 1111ms y el shader recién dibujaba a los
   2692 — un segundo y medio de tarjeta flotando sobre blanco.
   
   No se resuelve haciendo esperar a la tarjeta: si el navegador no tiene WebGL
   —o lo tiene bloqueado— el shader no dibuja NUNCA, y entonces la pantalla se
   quedaría vacía en vez de fea. Con el respaldo, ese caso también queda cubierto:
   sin WebGL el fondo es este, que se parece bastante.
   
   Los colores son los mismos tres del shader. Las posiciones acompañan a
   `shape="corners"` con el dibujo sin correr: masa arriba a la derecha y abajo a
   la izquierda. */
export const RESPALDO_AUTH = [
  'radial-gradient(65% 65% at 100% 0%, hsl(340 82% 52% / 0.75), hsl(14 100% 57% / 0.55) 38%, hsl(45 100% 51% / 0.30) 58%, transparent 76%)',
  'radial-gradient(65% 65% at 0% 100%, hsl(340 82% 52% / 0.60), hsl(14 100% 57% / 0.50) 38%, hsl(45 100% 51% / 0.32) 58%, transparent 76%)',
  'hsl(0 0% 100%)',
].join(', ');

/* El de la landing. Sigue a `FONDO_ANCHO`, que va con `rotation: 90`: esa
   rotación es la que corre las masas de `corners` fuera de la columna de texto y
   deja UNA sola, pegada a la esquina superior izquierda, arriba del titular.

   El respaldo tiene que respetar eso o es peor que no tenerlo. La primera versión
   ponía la masa arriba a la derecha y en el centro —copiada de cuando el fondo no
   estaba rotado— y durante los dos segundos que tarda el shader deshacía
   exactamente lo que la rotación arregla: color detrás de la navbar y de las
   maquetas. Si mañana cambia `rotation`, este degradado cambia con él. */
export const RESPALDO_LANDING = [
  'radial-gradient(46% 46% at 2% 0%, hsl(14 100% 57% / 0.55), hsl(45 100% 51% / 0.45) 34%, hsl(45 100% 51% / 0.16) 56%, transparent 72%)',
  'hsl(0 0% 100%)',
].join(', ');

/** `true` cuando la ventana es de escritorio. Se re-evalúa al rotar o achicar. */
export const usePantallaAncha = () => {
  const [ancha, setAncha] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const alCambiar = (e) => setAncha(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return ancha;
};

/**
 * Red de contención del shader.
 *
 * `GrainGradient` NO falla en silencio cuando no hay WebGL: lanza
 * "Paper Shaders: WebGL is not supported in this browser". Sin nada que lo
 * agarre, ese error sube y React desmonta el árbol entero, así que la pantalla
 * queda EN BLANCO — no un fondo feo, un login que no se puede usar. Pasa en
 * navegadores endurecidos, con la aceleración por hardware apagada, en algunos
 * WebView de Android y en máquinas viejas.
 *
 * Con la red, el fondo se cae solo y queda el respaldo de CSS, que es justamente
 * para lo que está.
 */
class RedDelShader extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fallo: false };
  }

  static getDerivedStateFromError() {
    return { fallo: true };
  }

  componentDidCatch(error) {
    // Aviso, no ruido: no se puede hacer nada al respecto y la página funciona.
    console.warn('El fondo animado no pudo iniciarse, queda el respaldo:', error?.message);
  }

  render() {
    return this.state.fallo ? null : this.props.children;
  }
}

/* EL INSTANTE EN QUE SE CONGELA LA ANIMACIÓN, en milisegundos desde el
   arranque. NO es un número arbitrario y no se puede poner en cero.

   Al detener el bucle, el shader pinta un cuadro concreto — y cuál sea cambia
   por completo dónde cae el color. El cuadro cero es el peor de todos: la
   mancha queda encima del titular. Medido barriendo dieciséis instantes y
   calculando la saturación media detrás del texto y en las dos esquinas:

     frame      detrás del texto      en las esquinas
      5200              0%                   85%     <- éste
     12500              1%                   90%
      4000              2%                   87%
      1500             23%                   72%
         0             42%                   69%     <- el que quedaba solo

   El criterio es el mismo con el que se eligió la posición: detrás del texto
   se quiere blanco —ahí la legibilidad la decide la SATURACIÓN, no el
   contraste de luminancia; un naranja fuerte puede pasar AA y aun así hacer
   ilegible un titular negro— y en las esquinas se quiere color, porque un
   fondo sin manchas no es sobrio, es plano.

   Para volver a medirlo: /dev/fondos acepta ?frame=N. */
const INSTANTE = 5200;

/* EL PRESUPUESTO DE PÍXELES POR CUADRO. Es LA palanca de rendimiento acá, y
   no es la velocidad.

   El shader redibuja el lienzo ENTERO en cada cuadro, así que lo que cuesta es
   el área, no cuán rápido se mueve: bajarle la velocidad no ahorra nada porque
   sigue redibujando sesenta veces por segundo. Y el motor apunta por defecto a
   `max(devicePixelRatio, 2)`, o sea que en una pantalla retina un hero de
   1512x860 se dibuja a 3024x1720 = 5,2 millones de píxeles por cuadro. Eso es
   lo que arrastraba la landing.

   `maxPixelCount` se aplica DESPUÉS del ratio y lo escala hacia abajo, así que
   funciona como tope duro y deja un costo por cuadro parejo en cualquier
   pantalla. 2,4 millones es ~1,6x en un portátil de 1512 y ~1,1x en un monitor
   de 1920: menos de la mitad del trabajo, sobre un degradado suave donde la
   diferencia no se ve. Se puede volver a barrer en /dev/fondos?px=N (millones).

   Lo que NO se hace es congelarlo. Se probó `speed={0}` y arregla el
   rendimiento, pero el fondo pierde justamente lo que lo hacía lindo. El
   movimiento es el punto; lo que había que abaratar es cada cuadro. */
const PRESUPUESTO = 2_400_000;

/**
 * @param speed           Velocidad de la deriva. No afecta el costo por cuadro.
 * @param frame           Instante inicial, en ms. Ver INSTANTE.
 * @param maxPixelCount   Tope de píxeles por cuadro. Ver PRESUPUESTO.
 */
/**
 * ¿Este navegador puede correr el shader?
 *
 * Se pregunta ANTES de montarlo, y no es lo mismo que la red de contención.
 * `GrainGradient` no falla en silencio: lanza. La red evita que ese error se
 * lleve puesta la página, pero el error igual OCURRE, y eso tiene dos costos que
 * preguntar primero evita: en desarrollo aparece el overlay rojo de CRA aunque
 * la página funcione —y check-ui lo lee, con razón, como error en pantalla— y en
 * producción queda ruido en cualquier reporte por algo que ya sabemos manejar.
 *
 * Se calcula una sola vez: crear contextos WebGL cuesta y la respuesta no cambia
 * en la vida de la pestaña.
 */
let soportaWebGL = null;
const hayWebGL = () => {
  if (soportaWebGL !== null) return soportaWebGL;
  try {
    const lienzo = document.createElement('canvas');
    soportaWebGL = Boolean(lienzo.getContext('webgl2') || lienzo.getContext('webgl'));
  } catch {
    soportaWebGL = false;
  }
  return soportaWebGL;
};


export default function FondoDeGranos({ posicion, respaldo, speed = 1, frame = INSTANTE, maxPixelCount = PRESUPUESTO, style }) {
  return (
    <>
      {/* Debajo del canvas y pintado con el primer cuadro: es lo que se ve
          mientras el shader arranca, y lo que queda si nunca arranca. */}
      {respaldo && (
        <div
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, background: respaldo, pointerEvents: 'none' }}
        />
      )}
      {hayWebGL() && (
        <RedDelShader>
        {/* OJO CON EL ORDEN: los valores por defecto van ANTES de `{...posicion}`
            y nada que la posición traiga va después. `posicion` puede traer
            `rotation`, y con `rotation={0}` escrito debajo del spread la
            constante quedaba pisada y el fondo volvía a la diagonal equivocada,
            con el color justo detrás del texto. Ya pasó una vez. */}
        <GrainGradient
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', ...style }}
          colorBack="hsl(0, 0%, 100%)"
          colors={['hsl(14, 100%, 57%)', 'hsl(45, 100%, 51%)', 'hsl(340, 82%, 52%)']}
          softness={0.76}
          intensity={0.45}
          noise={0}
          shape="corners"
          rotation={0}
          speed={speed}
          frame={frame}
          maxPixelCount={maxPixelCount}
          {...posicion}
        />
        </RedDelShader>
      )}
    </>
  );
}
